/**
 * F09 v2 — `/verifications/qa` tick QA page.
 *
 *   - Gallery: 5 entity types × 3 sizes × inline/hero, rendered under each of
 *     the 4 palettes. Each palette is a local scope (`data-theme` on the
 *     section + a nested `BraveMoveThemeProvider` that does NOT sync the
 *     document), so the admin's own theme is untouched.
 *   - Checklist: one checkbox per `VERIFICATION_RENDER_SURFACES` row, persisted
 *     per admin in localStorage (every access wrapped in try/catch — the page
 *     works without storage).
 *   - Live counts from `GET /admin/verification-qa` when available.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { BraveMoveThemeProvider, PALETTE_KEYS, PALETTE_VAR_MAP } from '@bravemove/design-tokens';
import { Badge, Card, Checkbox, ProgressBar, VerifiedTick } from '@bravemove/ui-web';
import {
  VERIFIABLE_ENTITY_TYPES,
  VERIFICATION_RENDER_SURFACES,
  type VerificationQaPayload,
  type VerificationRenderSurface,
} from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { PageHeader } from '../../components/PageHeader';
import { entityLabel } from '../../components/verification/labels';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';

const SIZES = ['sm', 'md', 'lg'] as const;
const STORAGE_PREFIX = 'bravemove.admin.verificationQa.v1.';

/**
 * Stable checklist key. Several registry rows share an `id`, and some share
 * `id + platform + fileHint` too (row 19 on mobile is listed once for `club`
 * and once for `organizer`), so the entity type and payload field are part of
 * the key — otherwise those rows collide (React key + one shared checkbox).
 */
function surfaceKey(row: VerificationRenderSurface): string {
  return `${row.id}|${row.platform}|${row.entityType}|${row.payloadField}|${row.fileHint}`;
}

function readChecks(storageKey: string): readonly string[] {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeChecks(storageKey: string, keys: readonly string[]): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(keys));
  } catch {
    // Storage unavailable (private mode / blocked) — the checklist still works in memory.
  }
}

/**
 * Re-bind the Tailwind colour tokens inside a palette scope. Tailwind v4
 * declares `--color-<t>: var(--bravemove-<t>)` on :root (theme.css @theme
 * block), so the value is resolved THERE and inherited already-substituted: a
 * nested `[data-theme]` only swaps the `--bravemove-*` inputs and every utility
 * (`bg-bg`, `text-info`, …) keeps the admin's own palette. Re-declaring the
 * bindings on the scoped section makes them resolve against its palette.
 *
 * Derived from `PALETTE_VAR_MAP` — the map `generateThemeCss()` builds the
 * theme.css @theme colour block from — so a colour token added there is
 * re-bound here automatically. Drift is guarded both ways: design-tokens'
 * `tailwind-preset.test.ts` checks every map entry is bound in theme.css, and
 * `apps/admin/test/verification-qa-rbac.test.tsx` checks every `--color-*`
 * binding in theme.css is re-bound here.
 */
const PALETTE_SCOPE_STYLE = Object.fromEntries(
  Object.values(PALETTE_VAR_MAP).map((bravemoveVar) => [
    bravemoveVar.replace('--bravemove-', '--color-'),
    `var(${bravemoveVar})`,
  ]),
) as CSSProperties;

function PaletteGallery({ palette }: { readonly palette: (typeof PALETTE_KEYS)[number] }) {
  const { t } = useI18n();
  return (
    <BraveMoveThemeProvider initial={palette} syncDocument={false}>
      <section
        data-theme={palette}
        style={PALETTE_SCOPE_STYLE}
        data-testid={`qa-palette-${palette}`}
        className="space-y-3 rounded-xl border border-hairline bg-bg p-4 text-ink"
      >
        <h3 className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
          {t('admin:verification.qa.palette', { palette })}
        </h3>
        <div className="space-y-3">
          {VERIFIABLE_ENTITY_TYPES.map((type) => (
            <div
              key={type}
              className="space-y-2 rounded-lg bg-surface p-3"
              data-testid={`qa-gallery-${palette}-${type}`}
            >
              <div className="flex flex-wrap items-center gap-4">
                {SIZES.map((size) => (
                  <span
                    key={size}
                    className="inline-flex items-center gap-1.5 text-ink"
                    title={t('admin:verification.qa.sizeInline', { size })}
                  >
                    <span className="font-semibold">
                      {t('admin:verification.qa.sampleName', { type: entityLabel(t, type) })}
                    </span>
                    <VerifiedTick entityType={type} size={size} />
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-start gap-3">
                {SIZES.map((size) => (
                  <div key={size} className="space-y-1">
                    <span className="font-mono text-[10px] text-ink-3">
                      {t('admin:verification.qa.sizeHero', { size })}
                    </span>
                    <VerifiedTick
                      entityType={type}
                      size={size}
                      variant="hero"
                      since="2026-01-15T00:00:00.000Z"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </BraveMoveThemeProvider>
  );
}

export function VerificationQaPage() {
  const { t } = useI18n();
  const { adminId } = useVerificationPermissions();
  const storageKey = `${STORAGE_PREFIX}${adminId ?? 'anonymous'}`;
  const [checked, setChecked] = useState<readonly string[]>(() => readChecks(storageKey));
  const [qa, setQa] = useState<VerificationQaPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminVerificationApi.qa().then(
      (payload) => {
        if (!cancelled) setQa(payload);
      },
      () => {
        // Counts are a nice-to-have; the gallery + checklist render without them.
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (key: string, on: boolean) => {
    const next = on ? [...checked.filter((k) => k !== key), key] : checked.filter((k) => k !== key);
    setChecked(next);
    writeChecks(storageKey, next);
  };

  const rows = VERIFICATION_RENDER_SURFACES;
  const done = rows.filter((r) => checked.includes(surfaceKey(r))).length;

  return (
    <div className="space-y-6" data-testid="verification-qa-page">
      <PageHeader
        title={t('admin:verification.pageTitle.qa')}
        subtitle={t('admin:verification.qa.subtitle')}
      />

      {qa ? (
        <Card>
          <section className="space-y-3" data-testid="qa-counts">
            <h2 className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {t('admin:verification.qa.counts')}
            </h2>
            <div className="grid gap-3 sm:grid-cols-5">
              {VERIFIABLE_ENTITY_TYPES.map((type) => {
                const sample = qa.samples[type];
                return (
                  <div key={type} className="space-y-1 rounded-lg bg-surface-2 p-3 text-[13px]">
                    <div className="font-semibold text-ink">{entityLabel(t, type)}</div>
                    <div className="text-ink-2">
                      {t('admin:verification.qa.verifiedCount', {
                        count: qa.counts[type].verified,
                      })}
                    </div>
                    <div className="text-ink-3">
                      {t('admin:verification.qa.openCount', {
                        count: qa.counts[type].openRequests,
                      })}
                    </div>
                    {sample ? (
                      <Link className="text-accent hover:underline" to={sample.deepLink}>
                        {sample.entityName}
                      </Link>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        </Card>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-[16px] font-bold text-ink">{t('admin:verification.qa.gallery')}</h2>
        <div className="grid gap-4 xl:grid-cols-2">
          {PALETTE_KEYS.map((palette) => (
            <PaletteGallery key={palette} palette={palette} />
          ))}
        </div>
      </section>

      <Card>
        <section className="space-y-3" data-testid="qa-checklist">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[16px] font-bold text-ink">
              {t('admin:verification.qa.checklist')}
            </h2>
            <span className="font-mono text-[12px] text-ink-2" data-testid="qa-progress">
              {t('admin:verification.qa.progress', { done, total: rows.length })}
            </span>
          </div>
          <p className="text-[12px] text-ink-3">{t('admin:verification.qa.checklistHelper')}</p>
          <ProgressBar
            value={rows.length === 0 ? 0 : (done / rows.length) * 100}
            label={t('admin:verification.qa.checklist')}
          />
          <ul
            className="divide-y divide-hairline"
            aria-label={t('admin:verification.qa.tableAria')}
          >
            {rows.map((row) => {
              const key = surfaceKey(row);
              return (
                <li
                  key={key}
                  className="flex flex-wrap items-start gap-3 py-2"
                  data-testid="qa-surface-row"
                  data-surface-key={key}
                >
                  <Checkbox
                    checked={checked.includes(key)}
                    onChange={(e) => toggle(key, e.target.checked)}
                    aria-label={row.surface}
                  />
                  <div className="min-w-0 flex-1 space-y-0.5 text-[13px]">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-[11px] text-ink-3">#{row.id}</span>
                      <span className="font-semibold text-ink">{row.surface}</span>
                      <Badge tone="neutral" size="sm">
                        {row.platform}
                      </Badge>
                      <Badge tone="info" size="sm">
                        {t('admin:verification.qa.track', { track: row.track })}
                      </Badge>
                      {row.entityType !== 'all' ? (
                        <Badge tone="neutral" size="sm">
                          {entityLabel(t, row.entityType)}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="break-all font-mono text-[11px] text-ink-3">{row.fileHint}</div>
                    <div className="font-mono text-[11px] text-ink-2">{row.payloadField}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </Card>
    </div>
  );
}
